import { Box, Button, CircularProgress, TextField } from "@material-ui/core";
import React from "react";
import { useForm } from "react-hook-form";
import { isEmail } from "validator";
import EmailIcon from "../../../../../components/icons/email";
import ProfileIcon from "../../../../../components/icons/profile";
import OrDevider from "../../../../../components/or-divider";
import { prettifyErrorMessage } from "../../../../../helpers/error";
import useCustomSnackbar from "../../../../../hooks/use-custom-snackbar";
import useTranslate from "../../../../../hooks/use-translate";
import {
  isEmailAlreadyRegistered,
  sendUserAuthenticationEmailOtp
} from "../../../../../services/user";
import CustomHeading from "./components/CustomHeading";

export type SignInEmailFormDataType = {
  email: string;
};

type Props = {
  onSubmit: (data: SignInEmailFormDataType) => void;
  onSwitchToRegister?: () => void;
  onSwitchToPhoneNumberSignIn?: () => void;
  labels?: {
    title?: string | false;
  };
};

export default function SignInEmailForm({
  onSubmit,
  labels,
  onSwitchToRegister,
  onSwitchToPhoneNumberSignIn
}: Props) {
  const t = useTranslate();
  const snackbar = useCustomSnackbar();

  const { handleSubmit, register, errors, formState } =
    useForm<SignInEmailFormDataType>({
      mode: "onSubmit",
      reValidateMode: "onSubmit"
    });

  const handleFormSubmit = async (data: SignInEmailFormDataType) => {
    try {
      await sendUserAuthenticationEmailOtp(data.email);
      onSubmit(data);
    } catch (e) {
      snackbar.showError(prettifyErrorMessage(e?.message));
    }
  };

  return (
    <>
      <Box mb={{ xs: 4, md: 6 }}>
        <CustomHeading
          dangerouslySetInnerHTML={{
            __html: labels?.title || t("authentication.signin")
          }}
        />
      </Box>

      <form
        onSubmit={handleSubmit(handleFormSubmit)}
        style={{
          width: "100%"
        }}
      >
        {/* Email */}
        <Box mb={3}>
          <TextField
            variant="outlined"
            id="email"
            name="email"
            autoComplete="email"
            fullWidth
            label={`${t("email")}`}
            placeholder={t("authentication.emailPlaceholder")}
            inputRef={register({
              required: t("error.required"),
              validate: {
                validFormat: (value: string) => {
                  if (!isEmail(value)) {
                    return t("error.invalidEmail");
                  }
                  return true;
                },
                notAlreadyRegistered: async (value: string) => {
                  const emailAlreadyRegistered = await isEmailAlreadyRegistered(
                    value
                  );
                  if (!emailAlreadyRegistered) {
                    return t("authentication.signin.noExistingEmail");
                  }
                  return true;
                }
              }
            })}
            error={!!errors.email}
            helperText={
              errors.email?.message ||
              t("authentication.signin.emailInstructions")
            }
            disabled={formState.isSubmitting}
            InputProps={{
              startAdornment: <EmailIcon />
            }}
          />
        </Box>

        <Button
          fullWidth
          variant="contained"
          color="primary"
          type="submit"
          disabled={formState.isSubmitting}
        >
          {!formState.isSubmitting && t("go")}
          {formState.isSubmitting && (
            <CircularProgress size={16} color="secondary" />
          )}
        </Button>

        <Box mt={2}>
          <OrDevider />
        </Box>

        {onSwitchToRegister && (
          <Box mt={2} display="flex" justifyContent="center">
            <Button
              variant="contained"
              onClick={onSwitchToRegister}
              fullWidth
              startIcon={<ProfileIcon />}
            >
              {t("authentication.registerInstead")}
            </Button>
          </Box>
        )}
      </form>

      {onSwitchToPhoneNumberSignIn && (
        <Box
          mt={4}
          display="flex"
          justifyContent="center"
          style={{ visibility: "hidden" }}
        >
          <Button
            variant="text"
            color="secondary"
            size="small"
            onClick={onSwitchToPhoneNumberSignIn}
          >
            {t("authentication.signInWithPhoneNumber")}
          </Button>
        </Box>
      )}
    </>
  );
}
